<script setup>
/**
 * @fileoverview ColorOptionGrid component for Custom Mosaics.
 *
 * Displays a responsive grid of selectable color swatches. Each swatch shows either
 * an image (if available) or a solid color fill. Supports:
 * - Single or multi-selection modes
 * - Maximum selection limits
 * - Custom column counts
 * - Sorting by order field from records
 * - Optional zoomed glaze-detail preview on hover / keyboard focus (zoomPreview)
 *
 * @component
 */
import { ref, computed, onBeforeUnmount } from 'vue';
import GlazeZoomPreview from '../GlazeZoomPreview.vue';

/**
 * Component props.
 * @property {Object} colors - Map of color names to hex values { name: hex }
 * @property {Object} [images] - Map of color names to image URLs { name: url }
 * @property {string[]} [selectedNames] - Array of currently selected color names
 * @property {number|null} [maxSelectable] - Maximum number of colors that can be selected
 * @property {number} [columns] - Number of grid columns (affects responsive layout)
 * @property {string} [ariaLabel] - Accessibility label for the grid
 * @property {Array} [records] - Full color records for sorting by order field
 */
const props = defineProps({
    colors: { type: Object, required: true },
    images: { type: Object, required: false, default: () => ({}) },
    selectedNames: { type: Array, required: false, default: () => [] },
    maxSelectable: { type: Number, required: false, default: null },
    columns: { type: Number, required: false, default: 3 },
    ariaLabel: { type: String, required: false, default: 'Color options' },
    records: { type: Array, required: false, default: () => [] },
    /** Show a zoomed glaze-detail preview when a swatch with a photo is hovered or focused */
    zoomPreview: { type: Boolean, required: false, default: false },
});

const emit = defineEmits(['select']);

/** { name, imageUrl, rect } of the swatch being previewed */
const preview = ref(null);

function hidePreview() {
    preview.value = null;
    window.removeEventListener('scroll', hidePreview, true);
}

function showPreview(name, el) {
    if (!props.zoomPreview || !props.images[name]) return;
    const rect = el.getBoundingClientRect();
    // Float past the sidebar so the preview doesn't cover neighboring swatches
    const sidebarRight = el.closest('aside')?.getBoundingClientRect().right ?? rect.right;
    preview.value = {
        name,
        imageUrl: props.images[name],
        rect: {
            top: rect.top,
            height: rect.height,
            left: rect.left,
            right: Math.max(rect.right, sidebarRight),
        },
    };
    // The anchor rect goes stale if anything scrolls
    window.addEventListener('scroll', hidePreview, true);
}

function onPointerEnter(e, name) {
    if (e.pointerType === 'touch') return;
    showPreview(name, e.currentTarget);
}

function onFocus(e, name) {
    // Keyboard focus only; a mouse click also focuses the button
    if (e.currentTarget.matches?.(':focus-visible')) showPreview(name, e.currentTarget);
}

onBeforeUnmount(hidePreview);

/**
 * Check if a color is currently selected.
 * @param {string} name - The color name to check
 * @returns {boolean} True if the color is selected
 */
const isSelected = (name) => props.selectedNames.includes(name);

/**
 * Check if a color should be disabled (max selections reached).
 * @param {string} name - The color name to check
 * @returns {boolean} True if the color should be disabled
 */
const isDisabled = (name) => {
    if (isSelected(name)) return false;
    if (props.maxSelectable == null) return false;
    // Allow swapping when only one selection is allowed (e.g., base color picker)
    if (props.maxSelectable === 1) return false;
    return props.selectedNames.length >= props.maxSelectable;
};

/**
 * CSS classes for the grid layout based on column count.
 * @type {import('vue').ComputedRef<string>}
 */
const gridClass = computed(() => {
    // Responsive sizing similar to PatternList.vue: tighter and denser on sm/md
    if (props.columns === 4) {
        return 'grid grid-cols-4 md:grid-cols-5 lg:grid-cols-4 gap-2 md:gap-3 lg:gap-4';
    }
    // Default/fallback: more columns on small/medium, fewer on large for larger tiles
    return 'grid grid-cols-4 md:grid-cols-5 lg:grid-cols-3 gap-2 md:gap-3 lg:gap-4';
});

/**
 * Handle color selection click.
 * @param {string} name - The color name that was clicked
 */
function handleSelect(name) {
    if (!isDisabled(name)) emit('select', name);
}

/**
 * Normalize a hex color value to lowercase with leading #.
 * @param {string} hex - The hex color value
 * @returns {string} Normalized hex value (e.g., '#abc123')
 */
function normalizeHex(hex) {
    const s = String(hex || '').trim();
    return (s.startsWith('#') ? s : `#${s}`).toLowerCase();
}

/**
 * Map of color names to their order values from records.
 * @type {import('vue').ComputedRef<Record<string, number|null>>}
 */
const nameToOrder = computed(() => {
    const map = {};
    for (const r of props.records) {
        if (r && r.name != null) map[r.name] = r.order ?? null;
    }
    return map;
});

/**
 * Color entries sorted by order (ascending, nulls last), then by hex value.
 * @type {import('vue').ComputedRef<Array<[string, string]>>}
 */
const sortedEntries = computed(() => {
    const entries = Object.entries(props.colors);
    return entries.sort(([nameA, hexA], [nameB, hexB]) => {
        const aOrder = nameToOrder.value[nameA];
        const bOrder = nameToOrder.value[nameB];
        const aHas = aOrder !== null && aOrder !== undefined;
        const bHas = bOrder !== null && bOrder !== undefined;
        if (aHas && bHas) {
            const diff = Number(aOrder) - Number(bOrder);
            if (diff !== 0) return diff;
        } else if (aHas && !bHas) {
            return -1;
        } else if (!aHas && bHas) {
            return 1;
        }
        // fallback secondary sort: by hex
        return normalizeHex(hexA).localeCompare(normalizeHex(hexB));
    });
});
</script>

<template>
    <div :class="gridClass" role="group" :aria-label="ariaLabel">
        <div v-for="[name, hex] in sortedEntries" :key="name" class="flex flex-col items-center">
            <button
                type="button"
                class="flex flex-col my-1 items-center justify-center border bg-white min-h-[52px] md:min-h-[60px] shadow-sm hover:shadow hover:border-dryline transition px-1.5 md:px-2 mx-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                :class="[
                    isSelected(name) ? 'border-dryline' : 'border-transparent',
                    isDisabled(name) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
                ]"
                :aria-pressed="isSelected(name) ? 'true' : 'false'"
                :aria-label="`${isSelected(name) ? 'Selected' : 'Select'} color ${name}`"
                :disabled="isDisabled(name)"
                @click="handleSelect(name)"
                @pointerenter="onPointerEnter($event, name)"
                @pointerleave="hidePreview"
                @focus="onFocus($event, name)"
                @blur="hidePreview"
            >
                <div
                    class="w-16 h-14 md:w-20 md:h-16 mb-1.5 md:mb-2 flex items-center justify-center overflow-hidden"
                >
                    <img
                        v-if="images[name]"
                        :src="images[name]"
                        :alt="name"
                        class="max-w-full max-h-full object-cover"
                        loading="lazy"
                        decoding="async"
                    />
                    <div
                        v-else
                        class="w-full h-full"
                        :style="{ backgroundColor: hex }"
                        aria-hidden="true"
                    />
                </div>
            </button>
            <span
                class="block w-full text-[11px] md:text-xs text-dryline text-center px-1 md:px-2 whitespace-normal"
            >
                {{ name }}
            </span>
        </div>
        <GlazeZoomPreview
            v-if="preview"
            :name="preview.name"
            :image-url="preview.imageUrl"
            :anchor-rect="preview.rect"
        />
    </div>
</template>
